import { BasketItem } from "../../../models/BasketItem";
import { TicketAccess } from "../../../models/TicketAccess";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import {
  ADD_TO_BASKET_URL,
  ASSIGN_ASSORT_TO_BASKET_URL,
  GET_BASKET_URL,
  GET_CALCULATE_PROM,
  GET_FOOD_LIST_URL,
  RESET_PROMOTION,
} from "../../../api/apiRoutes";
import { setBasketItemExercise, setBasketItemLimited } from "../../../models/BasketItem";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../../store";
import { useState } from "react";
import useFacility from "../../../../utils/hooks/useFacility";
import getId from "../../../../utils/getId";
import { updateBasketLength } from "../../../store/basket/basketActions";
import { Assort } from "../../../models/Assort";
import { AssortGroup } from "../../../models/AssortGroup";
import { setAddToBasketMessage } from "../../../store/alerts/alertsActions";
import format from "date-fns/format";
import useCustomHooks from "./useCustomHooks";
import { fakeSessionLogIn } from "../../../store/auth/authActions";
import { useTranslation } from "react-i18next";

interface BasketManager {
  deletePromotions: Function;
  basket: BasketItem[];
  total: number;
  getBasketForSelectedSeats: Function;
  getBasket: Function;
  addAssortToBasket: Function;
  getFoodList: Function;
  handleAddBasket: Function;
  prolongPass: Function;
  handleAddBasketLimited: Function;
  handleGetBasket: Function;
  promoCode: Function;
  foods: Assort[];
  validateBasket: Function;
  promoMessage: string;
  currentPromoCode: string;
  totalListPrice: number;
  checkAdditionalBasket: Function;
  clearBasket: Function;
  isLoading: boolean;
}

export const NO_LOGGED_ERROR_MESSAGE = "nie jest zalogowany";

export default function useBasketManager(): BasketManager {
  const { t } = useTranslation();
  const { get, post } = useApiRequest();
  const facilityIds = useFacility();
  const [basket, setBasket] = useState<BasketItem[]>([]);
  const dispatch = useDispatch();
  const [foods, setFoods] = useState<Assort[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [totalListPrice, setTotalListPrice] = useState<number>(0);
  const [promoMessage, setPromCode] = useState<string>("");
  const saleConfiguration = useSelector((state: State) => state.config.configuration);
  const { addGuideToBasket } = useCustomHooks();
  const [currentPromoCode, setCurrentPromoCode] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleGetBasket = async () => {
    getBasket();
  };

  const getBasket = async (skipLoading = false) => {
    if (!skipLoading) {
      setIsLoading(true);
    }
    const { result, isError, errorTable } = await get(GET_BASKET_URL);
    if (!isError) {
      setBasket(result.basket);
      setTotal(calculateBasket(result.basket));
      setTotalListPrice(result.totalListPrice);
      dispatch(updateBasketLength(result.basket.reduce((acc: number, item: BasketItem) => acc + 1 * item.qnt, 0)));
    } else {
      if (errorTable.some((item: string) => item.includes(NO_LOGGED_ERROR_MESSAGE))) {
        dispatch(fakeSessionLogIn());
      }
    }
    if (!skipLoading) {
      setIsLoading(false);
    }
  };

  const calculateBasket = (basket: BasketItem[]): number => {
    return basket.reduce((acc: number, item: BasketItem) => acc + item.price * item.qnt, 0);
  };

  const getBasketForSelectedSeats = (): BasketItem[] => {
    return basket;
  };

  const addAssortToBasket = async (assortIds: string, basketItem: BasketItem) => {
    const { isError } = await post(ASSIGN_ASSORT_TO_BASKET_URL, {
      ...basketItem,
      assortIds,
      facilityIds,
      qnt: 1,
    });
    if (!isError) {
      getBasket();
    }
  };

  const getFoodList = async (activeGroup: AssortGroup) => {
    const { result, isError } = await get(GET_FOOD_LIST_URL(facilityIds, getId(activeGroup)));
    if (!isError) {
      setFoods(result);
    }
  };

  const handleAddBasket = async (
    assortIds: string,
    assortName: string,
    price: number,
    ticketAccess?: TicketAccess,
    validFrom?: Date,
    qnt?: number
  ) => {
    setIsLoading(true);
    let formattedDate = format(new Date(), "yyyy-MM-dd");
    if (validFrom) {
      formattedDate = format(new Date(validFrom), "yyyy-MM-dd");
    }
    let quantity = 1;
    if (qnt) {
      quantity = qnt;
    }

    if (ticketAccess === null) {
      const basketItem = setBasketItemLimited(facilityIds, assortIds, formattedDate, quantity, null);
      const { isError } = await post(ADD_TO_BASKET_URL, {
        ...basketItem,
      });
      if (!isError) {
        await getBasket(true); // Skip loading in getBasket since we manage it here
        if (saleConfiguration.popupAfterAddTicket === true) {
          dispatch(setAddToBasketMessage(t("dodano_do_koszyka")));
        }
      }
    } else {
      const basketItem = setBasketItemExercise(facilityIds, assortIds, price, ticketAccess, formattedDate, quantity);
      const { isError } = await post(ADD_TO_BASKET_URL, {
        ...basketItem,
      });
      if (!isError) {
        await getBasket(true); // Skip loading in getBasket since we manage it here
        if (saleConfiguration.popupAfterAddTicket === true) {
          dispatch(setAddToBasketMessage(t("dodano_do_koszyka")));
        }
      }
    }
    setIsLoading(false);
  };

  const handleAddBasketLimited = async (assort: Assort, qnt: number, validFrom?: Date) => {
    let formattedDate = format(new Date(), "yyyy-MM-dd");
    if (validFrom) {
      formattedDate = format(validFrom, "yyyy-MM-dd");
    }
    let quantity = 1;
    if (qnt) {
      quantity = qnt;
    }

    const basketItem = setBasketItemLimited(facilityIds, getId(assort), formattedDate, quantity, []);
    const { isError } = await post(ADD_TO_BASKET_URL, {
      ...basketItem,
    });

    if (!isError) {
      await getBasket(true);
      if (saleConfiguration.popupAfterAddTicket === true) {
        dispatch(setAddToBasketMessage(t("dodano_do_koszyka")));
      }
    }
  };

  const prolongPass = async (assort: string, qnt: number, validFrom?: Date) => {
    let formattedDate = format(new Date(), "yyyy-MM-dd");
    if (validFrom) {
      formattedDate = format(validFrom, "yyyy-MM-dd");
    }
    let quantity = 1;
    if (qnt) {
      quantity = qnt;
    }
    const { isError } = await post(ADD_TO_BASKET_URL, {
      ...setBasketItemLimited(facilityIds, assort, formattedDate, quantity, []),
    });
    if (!isError) {
      getBasket();
      if (facilityIds === "1200" || facilityIds === "1204") {
        dispatch(setAddToBasketMessage(t("dodano_do_koszyka")));
      }
    }
  };

  const validateBasket = (): boolean => {
    const retValue = basket.find((item: BasketItem) => item.assortIds === "");
    if (retValue) {
      return true;
    }

    return false;
  };

  const promoCode = async (code: string) => {
    const { isError, errorTable } = await get(GET_CALCULATE_PROM(code, facilityIds));
    if (!isError) {
      setPromCode(t("podany_kod_jest_poprawny"));
      setCurrentPromoCode(code);
      getBasket();
    } else {
      setPromCode(errorTable[0]);
    }
  };

  const deletePromotions = async () => {
    await get(RESET_PROMOTION("", facilityIds));
    setCurrentPromoCode("");
  };

  // to metoda prewencyjna
  const checkAdditionalBasket = async (basketList: BasketItem[]) => {
    if (basketList.length > 0) {
      let assortName = "";
      let assortSecondName = "";
      let additionalAssortName = "";
      let additionalAssortId = 0;

      if (facilityIds === "1224") {
        assortName = "PRZEWODNIK GRUPY ZORGANIZOWANE ULGOWY OD 15 OS.";
        assortSecondName = "PRZEWODNIK GRUPY ZORGANIZOWANE OD 15 OS.";
        additionalAssortName = "PRZEWODNIK";
        additionalAssortId = 13;
      }

      if (!basketList.some((item: BasketItem) => item.assortName === additionalAssortName)) {
        if (
          basketList.some((item: BasketItem) => item.assortName === assortName) ||
          basketList.some((item: BasketItem) => item.assortName === assortSecondName)
        ) {
          addGuideToBasket(additionalAssortId, 1);
          setTimeout(() => {
            getBasket();
          }, 200);
        }
      }
      if (basketList.some((item: BasketItem) => item.assortName === additionalAssortName)) {
        if (
          !basketList.some((item: BasketItem) => item.assortName === assortName) &&
          !basketList.some((item: BasketItem) => item.assortName === assortSecondName)
        ) {
          addGuideToBasket(additionalAssortId, -1);
          setTimeout(() => {
            getBasket();
          }, 200);
        }
      }
    }
  };

  const clearBasket = async () => {
    setBasket([]);
    setTotal(0);
    setTotalListPrice(0);
    dispatch(updateBasketLength(0));
  };

  return {
    prolongPass,
    checkAdditionalBasket,
    deletePromotions,
    getBasket,
    promoMessage,
    promoCode,
    basket,
    total,
    totalListPrice,
    handleGetBasket,
    getBasketForSelectedSeats,
    addAssortToBasket,
    getFoodList,
    handleAddBasket,
    handleAddBasketLimited,
    foods,
    validateBasket,
    clearBasket,
    currentPromoCode,
    isLoading,
  };
}
